import React, { useEffect, useState } from "react";
import { api, speak } from "@/lib/kielo";
import { useI18n } from "@/i18n";
import { Volume2 } from "lucide-react";

export default function PictureDictionary() {
  const { t, lang } = useI18n();
  const [dict, setDict] = useState([]);
  const [scenes, setScenes] = useState([]);

  useEffect(() => {
    api.get("/dictionary", { params: { lang } }).then(({ data }) => setDict(data));
    api.get("/scenes", { params: { lang } }).then(({ data }) => setScenes(data));
  }, [lang]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0E382B] tracking-tight">{t("dict.title")}</h1>
      <p className="mt-2 text-[#52635B]">{t("dict.sub")}</p>

      {dict.map((cat) => (
        <section key={cat.id} className="mt-10" data-testid={`dict-category-${cat.id}`}>
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="font-serif text-2xl font-semibold text-[#0E382B]">{cat.category}</h2>
            <span className="text-sm text-[#52635B]">· {cat.categoryEn}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {cat.items.map((it) => (
              <button
                key={it.fi}
                onClick={() => speak(it.fi)}
                data-testid={`dict-word-${it.fi}`}
                className="group text-left bg-white rounded-2xl border border-[#E2DFD7] overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                style={{ boxShadow: "0 1px 2px rgba(14,56,43,0.04)" }}
              >
                <div className="aspect-square bg-[#F4F1EA] overflow-hidden">
                  <img src={it.img} alt={it.en} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-serif text-lg font-semibold text-[#16201B] leading-tight">{it.fi}</span>
                    <Volume2 className="w-4 h-4 text-[#1E429F] shrink-0" />
                  </div>
                  <span className="text-sm text-[#52635B]">{it.en}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* SCENES */}
      <div className="mt-16">
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0E382B]">{t("dict.scenesTitle")}</h2>
        <p className="mt-2 text-[#52635B]">{t("dict.scenesSub")}</p>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {scenes.map((sc) => (
            <div key={sc.id} data-testid={`scene-${sc.id}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <h3 className="font-serif text-xl font-semibold text-[#0E382B]">{sc.fi}</h3>
                <span className="text-sm text-[#52635B]">· {sc.title}</span>
              </div>
              {sc.guide && (
                <p className="mb-3 text-sm text-[#52635B] leading-relaxed bg-[#EDF2FE] border border-[#BFD3FE] rounded-xl px-4 py-3">{sc.guide}</p>
              )}
              <div className="relative rounded-2xl overflow-hidden border border-[#E2DFD7]">
                <img src={sc.img} alt={sc.title} className="w-full h-auto block" />
                {sc.labels.map((lb) => (
                  <button
                    key={lb.n}
                    onClick={() => speak(lb.fi)}
                    title={`${lb.fi} — ${lb.en}`}
                    data-testid={`scene-pin-${sc.id}-${lb.n}`}
                    className="absolute w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E46A28] text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md hover:scale-110 transition-transform"
                    style={{ left: `${lb.x}%`, top: `${lb.y}%` }}
                  >
                    {lb.n}
                  </button>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {sc.labels.map((lb) => (
                  <button
                    key={lb.n}
                    onClick={() => speak(lb.fi)}
                    className="flex items-center gap-2 rounded-xl bg-[#F9F8F5] border border-[#E2DFD7] px-2.5 py-2 text-left hover:border-[#B5B0A2]"
                  >
                    <span className="w-5 h-5 shrink-0 rounded-full bg-[#E46A28] text-white text-[10px] font-bold flex items-center justify-center">{lb.n}</span>
                    <span className="min-w-0">
                      <span className="block font-medium text-[#16201B] text-sm leading-tight truncate">{lb.fi}</span>
                      <span className="block text-xs text-[#52635B] truncate">{lb.en}</span>
                    </span>
                  </button>
                ))}
              </div>
              {sc.phrases && sc.phrases.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#0E382B] mb-2">{sc.fi} · useful phrases</p>
                  <div className="space-y-2">
                    {sc.phrases.map((ph, pi) => (
                      <button
                        key={pi}
                        onClick={() => speak(ph.fi)}
                        data-testid={`scene-phrase-${sc.id}-${pi}`}
                        className="w-full flex items-start gap-2 text-left rounded-xl bg-white border border-[#E2DFD7] px-3 py-2 hover:border-[#B5B0A2]"
                      >
                        <Volume2 className="w-4 h-4 text-[#1E429F] mt-0.5 shrink-0" />
                        <span className="min-w-0">
                          <span className="block font-medium text-[#16201B] text-sm">{ph.fi}</span>
                          <span className="block text-xs text-[#52635B]">{ph.en}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
